/**
 * Edit calculation — /settings/calculations/$key
 *
 * Loads the calculation, prefills the editor, saves via update
 * mutation. Also surfaces a link to the run page and shows the
 * most recent runs inline.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Play, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  CalculationEditor,
  type CalculationFormValue,
  type CalculationInputDraft,
} from '@/components/custom/calculation-editor'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/calculations/$key')({
  component: EditCalculationPage,
})

function EditCalculationPage() {
  const { key } = Route.useParams()
  const queryClient = useQueryClient()

  const { data: calc, isLoading } = useQuery(
    orpc.extensions.calculations.get.queryOptions({ input: { key } }),
  )

  const { data: runs } = useQuery(
    orpc.extensions.calculations.runs.queryOptions({
      input: { key, limit: 10 },
    }),
  )

  const [form, setForm] = useState<CalculationFormValue | null>(null)

  // Hydrate once per calculation identity — see P2.4 discussion of
  // why we key off id, not the whole object reference.
  useEffect(() => {
    if (!calc) return
    setForm({
      key: calc.key,
      name: calc.name,
      description: calc.description ?? '',
      moduleKey: calc.moduleKey,
      inputs: calc.inputs as CalculationInputDraft[],
      body: calc.body,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc?.id])

  const updateMutation = useMutation(
    orpc.extensions.calculations.update.mutationOptions({
      onSuccess: () => {
        toast.success('Calculation saved')
        void queryClient.invalidateQueries({
          queryKey: orpc.extensions.calculations.get.queryKey({ input: { key } }),
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  if (isLoading || !form) {
    return <Skeleton className="h-96 w-full" />
  }

  const handleSave = () => {
    updateMutation.mutate({
      key: form.key,
      name: form.name,
      description: form.description || null,
      moduleKey: form.moduleKey,
      inputs: form.inputs,
      body: form.body,
    })
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
      <Link
        to="/settings/calculations"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to calculations
      </Link>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>{form.name}</CardTitle>
            <p className="text-xs font-mono text-muted-foreground mt-1">{form.key}</p>
          </div>
          <Link to="/settings/calculations/$key/run" params={{ key }}>
            <Button variant="outline" size="sm">
              <Play className="h-4 w-4 mr-1" />
              Run
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <CalculationEditor value={form} onChange={setForm} isEdit />
          <div className="flex justify-end gap-2 pt-4 border-t mt-6">
            <Button
              type="button"
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent runs — simple list for debugging */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent runs</CardTitle>
        </CardHeader>
        <CardContent>
          {!runs || runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No runs yet. Try it out from the run page.
            </p>
          ) : (
            <ul className="divide-y">
              {runs.map((run) => (
                <li key={run.id} className="py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        run.status === 'success'
                          ? 'default'
                          : run.status === 'timeout'
                            ? 'secondary'
                            : 'destructive'
                      }
                    >
                      {run.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(run.startedAt).toLocaleString()}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      by {run.triggeredByType}
                    </span>
                  </div>
                  {run.error && (
                    <div className="text-xs text-destructive mt-1">{run.error}</div>
                  )}
                  {run.outputs && (
                    <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                      {JSON.stringify(run.outputs, null, 2)}
                    </pre>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
